import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";

interface GstLineItem {
  gst?: number;
  quantity: number;
  taxableAmount: number;
  taxAmount: number;
  grossAmount: number;
}

// Draws the GST Summary table and returns the Y position after it.
export const addGstSummaryTable = (
  doc: jsPDF,
  items: GstLineItem[],
  startY: number,
  leftCol: number
): number => {
  const grouped = new Map<number, { qty: number; value: number; tax: number; total: number }>();
  items.forEach((item) => {
    const rate = Number(item.gst ?? 0);
    const e = grouped.get(rate) ?? { qty: 0, value: 0, tax: 0, total: 0 };
    e.qty += Number(item.quantity || 0);
    e.value += Number(item.taxableAmount || 0);
    e.tax += Number(item.taxAmount || 0);
    e.total += Number(item.grossAmount || 0);
    grouped.set(rate, e);
  });

  const rows = Array.from(grouped.entries()).sort((a, b) => a[0] - b[0]);
  const blue = { fontStyle: "bold" as const, textColor: [0, 0, 255] as [number, number, number] };

  let yPos = startY + 10;
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("GST Summary", leftCol, yPos);
  doc.setFont("helvetica", "normal");
  yPos += 8;

  if (rows.length === 0) {
    doc.setFontSize(10);
    doc.text("No GST breakdown available.", leftCol, yPos);
    return yPos + 6;
  }

  const body: RowInput[] = rows.map(([rate, e]) => [
    `${rate.toFixed(2)}%`,
    e.qty.toString(),
    e.value.toFixed(2),
    e.tax.toFixed(2),
    e.total.toFixed(2),
  ]);

  const sum = (k: "qty" | "value" | "tax" | "total") => rows.reduce((s, [, e]) => s + e[k], 0);
  body.push([
    { content: "Total", styles: blue },
    { content: sum("qty").toString(), styles: blue },
    { content: sum("value").toFixed(2), styles: blue },
    { content: sum("tax").toFixed(2), styles: blue },
    { content: sum("total").toFixed(2), styles: blue },
  ]);

  autoTable(doc, {
    head: [["GST %", "Qty", "Value without Tax (Rs)", "Tax Amount (Rs)", "Total Amount (Rs)"]],
    body,
    startY: yPos,
    headStyles: { textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "center" },
    },
    styles: { cellPadding: 3, fontSize: 10, valign: "middle" },
  });

  return (doc as any).lastAutoTable?.finalY ?? yPos + 10;
};
